import React, { useState } from 'react'
import Card from '@material-ui/core/Card'
import Typography from '@material-ui/core/Typography'
import CardActions from '@material-ui/core/CardActions'
import CardMedia from '@material-ui/core/CardMedia'
import MaterialLink from '@material-ui/core/Link'
import CardContent from '@material-ui/core/CardContent'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { components } from '@plandi/common/src/generated'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Button from '@material-ui/core/Button'
import clsx from 'clsx'

import { TranslationDictionary, moneyFormat, useLocale } from '@plandi/common'
import { ReactComponent as PersonIcon } from '../../../../assets/personiconproject.svg'
import { URLS } from 'src/common'
import { useProjectCardStyles } from './project-card.styles'
import { ProjectCardFavoriteIcon } from './project-card-favorite-icon'
import { ProjectAuthorMenu } from '../project-author-menu'
import { Tooltip, useTheme } from '@material-ui/core'
import moment from 'moment'
import Image from 'next/image'

type ProjectCardProps = components['schemas']['ProjectMarketGetResource']
type Translate = {
  contact?: boolean
  contactData?: components['schemas']['ProfileContactResource']
  translate?: TranslationDictionary['data']
  locale: string
}

export const ProjectCard: React.FC<ProjectCardProps & Translate> = (props) => {
  const classes = useProjectCardStyles()
  const [isHovered, setIsHovered] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down(600))
  const router = useRouter()
  const locale = useLocale()
  const renderActionIcon = () => {
    if (props.isAuthor) {
      return (
        <ProjectAuthorMenu
          className={classes.menu}
          projectId={props.id}
          status="published"
          slugName={props.slug_name}
          translate={props.translate}
        />
      )
    }

    return (
      <ProjectCardFavoriteIcon
        className={classes.menu}
        isFavorite={props.isFavorite}
        projectId={props.id}
        translate={props.translate}
      />
    )
  }

  const handleChangeRouteProjectsUser = (e: { preventDefault: () => void }) => {
    e.preventDefault()
    if (props.userId) {
      router.push({
        pathname:
          props.type === 'user' ? URLS.projects.user : URLS.projects.profile,
        query: {
          userId: props.type === 'user' ? props.userId : props.profile?.id,
          type: props.type
        }
      })
    }
  }

  return (
    <div className="position">
      <Card elevation={0} className={classes.root}>
        <CardContent
          onPointerEnter={() =>
            setIsHovered((prev) => (!isMobile ? true : prev))
          }
          onPointerLeave={() =>
            setIsHovered((prev) => (!isMobile ? false : prev))
          }
          onClick={() => setIsHovered((prev) => (isMobile ? !prev : prev))}
          className={classes.content}
          style={{ paddingBottom: 20 }}
        >
          <Link
            href={{
              pathname: URLS.projects.detail,
              query: {
                projectId: props.id,
                slugName: encodeURIComponent(props.slug_name || 'no-slug-name')
              }
            }}
            passHref
          >
            <a href="/">
              <CardMedia
                component="img"
                className={classes.img}
                src={props.cover ?? ''}
                title={props.name ?? ''}
              />
            </a>
          </Link>
          {renderActionIcon()}
          <Link
            href={{
              pathname: URLS.projects.detail,
              query: {
                projectId: props.id,
                slugName: encodeURIComponent(props.slug_name || 'no-slug-name')
              }
            }}
            passHref
          >
            <a href="/" className={classes.nameBlock}>
              {props.name}
            </a>
          </Link>
          <div className={classes.info}>
            <Typography
              variant="body2"
              color="textSecondary"
              component="span"
              noWrap
            >
              {props.buildingArea ? (
                <>
                  {' '}
                  {moneyFormat(props.buildingArea, false, locale)}{' '}
                  {`${props.locale === 'ru' ? 'м' : 'm'}2`}
                </>
              ) : (
                props.translate?.['area-not-specified']
              )}
            </Typography>
            <CardActions className={classes.actions} disableSpacing>
              <Typography className={classes.cost}>
                {props.cost && moneyFormat(props.cost, true, locale)}
              </Typography>
            </CardActions>
          </div>
          <div className={classes.dateBlock}>
            <Typography
              variant="body2"
              noWrap
              color="textSecondary"
              style={{ fontSize: 12, paddingRight: 5 }}
            >
              {`${props.translate?.['date-publish']}:`}
            </Typography>
            <Typography
              variant="body2"
              noWrap
              color="primary"
              style={{ fontSize: 12 }}
            >
              {moment(props.publishedAt?.date).format('DD.MM.YYYY')}
            </Typography>
          </div>
          <div
            className={clsx(classes.hoverContent, {
              [classes.hoverContentVisible]: isHovered
            })}
          >
            {isHovered ? (
              <Link
                href={{
                  pathname: URLS.projects.detail,
                  query: {
                    projectId: props.id,
                    slugName: encodeURIComponent(
                      props.slug_name || 'no-slug-name'
                    )
                  }
                }}
                passHref
              >
                <a href="/">
                  <Typography
                    variant="body2"
                    noWrap
                    className={classes.name}
                    onClick={handleChangeRouteProjectsUser}
                    component={MaterialLink}
                    underline="hover"
                  >
                    {props.contact ? (
                      <div className={classes.fullName}>
                        {!props?.contactData?.avatarUrl ? (
                          <PersonIcon className={classes.iconName} />
                        ) : (
                          <Image
                            width={32}
                            height={32}
                            src={props?.contactData?.avatarUrl}
                            alt=""
                            className={classes.avatarUrl}
                          />
                        )}
                        <Tooltip title={props?.contactData?.fullName ?? ''}>
                          <div className={classes.fullName}>
                            {props?.contactData?.fullName}
                          </div>
                        </Tooltip>
                      </div>
                    ) : props.type === 'freelancer' ? (
                      <div className={classes.fullName}>
                        {!props?.profile?.avatarUrl ? (
                          <PersonIcon className={classes.iconName} />
                        ) : (
                          <Image
                            width={32}
                            height={32}
                            src={props?.profile?.avatarUrl}
                            alt=""
                            className={classes.avatarUrl}
                          />
                        )}
                        <Tooltip title={props?.profile?.name ?? ''}>
                          <div className={classes.fullName}>
                            {props?.profile?.name}
                          </div>
                        </Tooltip>
                      </div>
                    ) : props.type === 'company' ? (
                      <div className={classes.fullName}>
                        {!props?.profile?.avatarUrl ? (
                          <PersonIcon className={classes.iconName} />
                        ) : (
                          <Image
                            width={32}
                            height={32}
                            src={props?.profile?.avatarUrl}
                            alt=""
                            className={classes.avatarUrl}
                          />
                        )}
                        <Tooltip title={props?.profile?.name ?? ''}>
                          <div className={classes.fullName}>
                            {props?.profile?.name}
                          </div>
                        </Tooltip>
                      </div>
                    ) : (
                      <div className={classes.fullName}>
                        {!props?.avatarUrl ? (
                          <PersonIcon className={classes.iconName} />
                        ) : (
                          <Image
                            width={32}
                            height={32}
                            src={props?.avatarUrl}
                            alt=""
                            className={classes.avatarUrl}
                          />
                        )}
                        <Tooltip title={props?.fullName ?? ''}>
                          <div className={classes.fullName}>
                            {props?.fullName}
                          </div>
                        </Tooltip>
                      </div>
                    )}
                  </Typography>
                  <div>
                    {props?.type === 'user' ? (
                      <Typography variant="caption">
                        {props.translate?.['blog-site']?.['private-person'] ??
                          'Частное лицо'}
                      </Typography>
                    ) : props?.type === 'freelancer' ? (
                      <Typography variant="caption">
                        {props.translate?.freelancer ?? 'Фрилансер'}
                      </Typography>
                    ) : (
                      <Typography variant="caption">
                        {props.translate?.company ?? 'Компания'}
                      </Typography>
                    )}
                  </div>
                  <div className={classes.info}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      component="span"
                      noWrap
                    >
                      {props.buildingArea ? (
                        <>
                          {' '}
                          {moneyFormat(props.buildingArea, false, locale)}{' '}
                          {`${props.locale === 'ru' ? 'м' : 'm'}2`}
                        </>
                      ) : (
                        props.translate?.['area-not-specified']
                      )}
                    </Typography>
                    <CardActions className={classes.actions} disableSpacing>
                      <Typography className={classes.cost}>
                        {props.cost && moneyFormat(props.cost, true, locale)}
                      </Typography>
                    </CardActions>
                  </div>
                  <div className={classes.dateBlock}>
                    <Typography
                      variant="body2"
                      noWrap
                      color="textSecondary"
                      style={{ fontSize: 12, paddingRight: 5 }}
                    >
                      {`${props.translate?.['date-publish']}:`}
                    </Typography>
                    <Typography
                      variant="body2"
                      noWrap
                      color="primary"
                      style={{ fontSize: 12 }}
                    >
                      {moment(props.publishedAt?.date).format('MM.DD.YYYY')}
                    </Typography>
                  </div>
                  <div className={classes.buttonPosition}>
                    <Link
                      href={{
                        pathname: URLS.projects.detail,
                        query: {
                          projectId: props.id,
                          slugName: encodeURIComponent(
                            props.slug_name || 'no-slug-name'
                          )
                        }
                      }}
                      passHref
                    >
                      <a href="/">
                        <Button
                          variant="contained"
                          color="primary"
                          className={classes.button}
                          size="small"
                        >
                          {isMobile
                            ? props.translate?.['to-view']
                            : props.translate?.shop['projects-page']['view']}
                        </Button>
                      </a>
                    </Link>
                  </div>
                </a>
              </Link>
            ) : (
              <>
                <Typography
                  variant="body2"
                  noWrap
                  className={classes.name}
                  onClick={handleChangeRouteProjectsUser}
                  component={MaterialLink}
                  underline="hover"
                >
                  {props.contact ? (
                    <div className={classes.fullName}>
                      {!props?.contactData?.avatarUrl ? (
                        <PersonIcon className={classes.iconName} />
                      ) : (
                        <Image
                          width={32}
                          height={32}
                          src={props?.contactData?.avatarUrl}
                          alt=""
                          className={classes.avatarUrl}
                        />
                      )}
                      <Tooltip title={props?.contactData?.fullName ?? ''}>
                        <div className={classes.fullName}>
                          {props?.contactData?.fullName}
                        </div>
                      </Tooltip>
                    </div>
                  ) : props.type === 'freelancer' ? (
                    <div className={classes.fullName}>
                      {!props?.profile?.avatarUrl ? (
                        <PersonIcon className={classes.iconName} />
                      ) : (
                        <Image
                          width={32}
                          height={32}
                          src={props?.profile?.avatarUrl}
                          alt=""
                          className={classes.avatarUrl}
                        />
                      )}
                      <Tooltip title={props?.profile?.name ?? ''}>
                        <div className={classes.fullName}>
                          {props?.profile?.name}
                        </div>
                      </Tooltip>
                    </div>
                  ) : props.type === 'company' ? (
                    <div className={classes.fullName}>
                      {!props?.profile?.avatarUrl ? (
                        <PersonIcon className={classes.iconName} />
                      ) : (
                        <Image
                          width={32}
                          height={32}
                          src={props?.profile?.avatarUrl}
                          alt=""
                          className={classes.avatarUrl}
                        />
                      )}
                      <Tooltip title={props?.profile?.name ?? ''}>
                        <div className={classes.fullName}>
                          {props?.profile?.name}
                        </div>
                      </Tooltip>
                    </div>
                  ) : (
                    <div className={classes.fullName}>
                      {!props?.avatarUrl ? (
                        <PersonIcon className={classes.iconName} />
                      ) : (
                        <Image
                          width={32}
                          height={32}
                          src={props?.avatarUrl}
                          alt=""
                          className={classes.avatarUrl}
                        />
                      )}
                      <Tooltip title={props?.fullName ?? ''}>
                        <div className={classes.fullName}>
                          {props?.fullName}
                        </div>
                      </Tooltip>
                    </div>
                  )}
                </Typography>
                <div>
                  {props?.type === 'user' ? (
                    <Typography variant="caption">
                      {props.translate?.['blog-site']?.['private-person'] ??
                        'Частное лицо'}
                    </Typography>
                  ) : props?.type === 'freelancer' ? (
                    <Typography variant="caption">
                      {props.translate?.freelancer ?? 'Фрилансер'}
                    </Typography>
                  ) : (
                    <Typography variant="caption">
                      {props.translate?.company ?? 'Компания'}
                    </Typography>
                  )}
                </div>
                <div className={classes.info}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    component="span"
                    noWrap
                  >
                    {props.buildingArea ? (
                      <>
                        {' '}
                        {moneyFormat(props.buildingArea, false, locale)}{' '}
                        {`${props.locale === 'ru' ? 'м' : 'm'}2`}
                      </>
                    ) : (
                      props.translate?.['area-not-specified']
                    )}
                  </Typography>
                  <CardActions className={classes.actions} disableSpacing>
                    <Typography className={classes.cost}>
                      {props.cost && moneyFormat(props.cost, true, locale)}
                    </Typography>
                  </CardActions>
                </div>
                <div className={classes.dateBlock}>
                  <Typography
                    variant="body2"
                    noWrap
                    color="textSecondary"
                    style={{ fontSize: 12, paddingRight: 5 }}
                  >
                    {`${props.translate?.['date-publish']}:`}
                  </Typography>
                  <Typography
                    variant="body2"
                    noWrap
                    color="primary"
                    style={{ fontSize: 12 }}
                  >
                    {moment(props.publishedAt?.date).format('MM.DD.YYYY')}
                  </Typography>
                </div>
                <div className={classes.buttonPosition}>
                  <Link
                    href={{
                      pathname: URLS.projects.detail,
                      query: {
                        projectId: props.id,
                        slugName: encodeURIComponent(
                          props.slug_name || 'no-slug-name'
                        )
                      }
                    }}
                    passHref
                  >
                    <a href="/">
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        size="small"
                      >
                        {props.translate?.shop['projects-page']['view']}
                      </Button>
                    </a>
                  </Link>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
